import React, { CSSProperties, FunctionComponent } from 'react';
import { CheckBox as CheckedIcon, CheckBoxOutlineBlank as UncheckedIcon, IndeterminateCheckBox as IndeterminateIcon } from '../../components/icons';
import cn from '../cn';
import { cssVars as palette } from '../../theme/cssVars';

type CheckboxColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

type CheckboxUIProps = {
  checked?: boolean,
  indeterminate?: boolean,
  onChange?: (e: any) => void,
  onClick?: (e: React.MouseEvent) => void,
  onMouseDown?: (e: React.MouseEvent) => void,
  color?: CheckboxColor,
  size?: 'small' | 'medium',
  disabled?: boolean,
  name?: string,
  className?: string,
  style?: CSSProperties,
  inputProps?: { [k: string]: any },
};

const mainColor: Record<CheckboxColor, string> = {
  default: 'var(--ds-text-muted)',
  primary: 'var(--ds-accent)',
  secondary: palette.secondary,
  error: palette.error,
  info: palette.info,
  success: palette.success,
  warning: palette.warning,
};

// Unchecked / disabled glyph colours come from the design-system tokens so the
// control tracks the active theme instead of a fixed black alpha.
const UNCHECKED_COLOR = 'var(--ds-text-muted)';
const DISABLED_COLOR = 'var(--ds-text-subtle)';

// MUI Checkbox port using the retained @mui/icons-material glyphs (identical look).
// Controlled `checked`; fires both onClick (real event) and onChange (synthetic
// with target.checked) on toggle, matching MUI. `style.color` overrides the glyph
// color (e.g. ToolsLateReturns' green tick).
const CheckboxUI: FunctionComponent<CheckboxUIProps> = ({
  checked = false,
  indeterminate = false,
  onChange,
  onClick,
  onMouseDown,
  color = 'primary',
  size = 'medium',
  disabled = false,
  name,
  className = '',
  style,
  inputProps,
}) => {
  const px = size === 'small' ? 16 : 18;
  const computed = disabled
    ? DISABLED_COLOR
    : checked || indeterminate ? mainColor[color] : UNCHECKED_COLOR;
  const glyphColor = (style?.color as string) ?? computed;

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;
    onClick?.(e);
    onChange?.({ target: { checked: !checked, name }, currentTarget: { checked: !checked } });
  };

  const Icon = indeterminate ? IndeterminateIcon : checked ? CheckedIcon : UncheckedIcon;

  return (
    <span
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={inputProps?.['aria-label']}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange?.({ target: { checked: !checked, name }, currentTarget: { checked: !checked } });
        }
      }}
      onMouseDown={onMouseDown}
      className={cn('inline-flex items-center justify-center p-1', disabled ? 'cursor-default' : 'cursor-pointer', className)}
      style={style}
    >
      <Icon style={{ fontSize: px, color: glyphColor }} />
    </span>
  );
};

export default CheckboxUI;
